import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { type VisitTrafficTrendType } from '@/api/common';
import Flare, {
  Area,
  Axis,
  type FlareError,
  type FlareSeriesOptions,
  Legend,
  Line,
  Notes,
  Tooltip,
} from '@/components/Flare';
import { TAM_PRIMARY, VISITS_PRIMARY } from '@/constants/colors';
import { MonthDateShortFormat, MonthDateYearFormat } from '@/constants/formats';
import { getUTCTime } from '@/helper/dateHelper';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  currentPeriodData?: VisitTrafficTrendType[];
  previousPeriodData?: VisitTrafficTrendType[];
  isLoading?: boolean;
  error: FlareError;
};

const TrafficEfficiencyTrendChart = ({
  currentPeriodData,
  previousPeriodData,
  isLoading,
  error,
}: Props) => {
  const previousPeriodAverageBaseline = useMemo(() => {
    if (!previousPeriodData) {
      return undefined;
    }

    return (
      previousPeriodData.reduce(
        (acc, d) => (d.allVisits === 0 ? acc : acc + d.targetVisits / d.allVisits),
        0,
      ) / previousPeriodData.length
    );
  }, [previousPeriodData]);

  const seriesData: FlareSeriesOptions[] | undefined = useMemo(() => {
    if (!currentPeriodData) {
      return undefined;
    }

    return [
      {
        id: 'currentPeriod',
        name: t`Efficiency %`,
        zIndex: 4,
        data: currentPeriodData.map((d) => ({
          date: d.sundayOfWeek,
          amount: d.allVisits === 0 ? 0 : d.targetVisits / d.allVisits,
        })),
      },
    ];
  }, [currentPeriodData]);

  const previousPeriodBaselineData = useMemo(() => {
    if (!currentPeriodData) {
      return undefined;
    }

    return [
      {
        id: 'previousPeriod',
        name: t`Previous Period Average %`,
        zIndex: 5,
        dashStyle: 'Dash',
        data: currentPeriodData.map((d) => ({
          date: d.sundayOfWeek,
          previousPeriod: previousPeriodAverageBaseline,
        })),
      },
    ];
  }, [currentPeriodData, previousPeriodAverageBaseline]);

  return (
    <Flare
      data={seriesData}
      width="auto"
      height="auto"
      parseX={getUTCTime}
      description={t`An efficiency chart showing the efficiency of TAM visits compared to the baseline efficiency from the previous period`}
      isLoading={isLoading}
      error={error}
    >
      <Legend noToggleVisibility />
      <Area
        data={seriesData}
        x="date"
        y="amount"
        position="overlap"
        fillOpacity={0.85}
        colors={[TAM_PRIMARY]}
      />
      <Line
        data={previousPeriodBaselineData}
        x="date"
        y="previousPeriod"
        colors={[VISITS_PRIMARY]}
        hideLoader
      />
      <Axis
        position="left"
        labelFormat={(item) => numberFormat(item.value, { isPercent: true, precision: 0 })}
      />
      <Axis
        type="datetime"
        position="bottom"
        crosshair="line"
        labelFormat={(item) => dayjs(item.value).utc().format(MonthDateShortFormat)}
      />
      <Tooltip
        shared
        titleFormat={(point) => dayjs(point?.x).format(MonthDateYearFormat)}
        rowValueFormat={(point) => numberFormat(point?.y, { isPercent: true, precision: 1 })}
      />
      <Notes />
    </Flare>
  );
};

export default TrafficEfficiencyTrendChart;
